"use client";

import * as React from "react";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import { StepButton, StepIconProps } from "@mui/material";
import { Step as StepType } from "types/homepage";
import { useTranslation } from "@i18n";

function buildStepIcon(isArabic: boolean) {
  return function CustomStepIcon(props: StepIconProps) {
    const { active, icon } = props;
    const display =
      typeof icon === "number"
        ? isArabic
          ? icon.toLocaleString("ar-EG")
          : String(icon)
        : String(icon ?? "");

    // Active step gets the brand teal; every other step (past or future) gets
    // a solid black fill. Numbers are white in both states.
    const fill = active ? "#14B2B6" : "#000000";

    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 32,
          height: 32,
          borderRadius: "50%",
          backgroundColor: fill,
          color: "#ffffff",
          fontSize: "0.9rem",
          fontWeight: 600,
          transition: "background-color 0.3s",
          lineHeight: 1,
        }}
      >
        {display}
      </span>
    );
  };
}

export default function VerticalLinearStepper(props: {
  steps: StepType[];
  step: number;
  setStep: (step: number) => void;
  stepNames?: readonly string[];
}) {
  const { step, setStep, stepNames } = props;
  const { isRtl } = useTranslation();
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => {
    setMounted(true);
  }, []);

  const handleStep = (step: number) => () => {
    setStep(step);
  };

  const StepIcon = React.useMemo(() => buildStepIcon(isRtl), [isRtl]);

  if (!mounted) {
    return null;
  }

  return (
    <Box
      sx={{
        width: { xs: "40vw", sm: "15vw" },
        height: "100%",
      }}
    >
      <Stepper
        activeStep={step}
        orientation="vertical"
        sx={{
          height: "100%",
          ".MuiStepConnector-line": { height: "100%" },
          // Circles are 32px, so the connector margin is half that (16px) to
          // put the line dead-center under each circle.
          ".MuiStepConnector-vertical": {
            marginLeft: isRtl ? 0 : "16px",
            marginRight: isRtl ? "16px" : 0,
          },
          // In RTL the line also needs to paint on the connector's right edge
          // instead of the left (MUI defaults to border-left for vertical).
          ...(isRtl && {
            ".MuiStepConnector-lineVertical": {
              borderLeftStyle: "none",
              borderRightWidth: "1px",
              borderRightStyle: "solid",
              borderColor: "#bdbdbd",
            },
          }),
        }}
      >
        {props.steps.map((stepItem, index) => (
          <Step key={stepItem.id}>
            <StepButton
              color="inherit"
              onClick={handleStep(index)}
              disabled={false}
              icon={<StepIcon icon={index + 1} active={step === index} completed={step > index} />}
              className="text-midPrimary"
              sx={{
                ".MuiStepLabel-iconContainer": isRtl
                  ? {
                      // Zero out the default right padding (MUI applies it
                      // physically, so it doesn't flip in RTL and would push
                      // the icon 8px off-center from the connector line).
                      paddingLeft: "1rem",
                      paddingRight: 0,
                    }
                  : {
                      paddingRight: "0.5rem",
                    },
                ".MuiStepLabel-label": {
                  fontSize: "0.9rem",
                  color: "#6b7280",
                  cursor: "pointer",
                  // Wrap onto multiple lines respecting the locale direction —
                  // in RTL we want the second line aligned to the right.
                  textAlign: isRtl ? "right" : "left",
                },
                ".MuiStepLabel-label.Mui-active": {
                  color: "#14B2B6",
                  fontWeight: 700,
                },
              }}
            >
              {stepNames?.[index] ?? stepItem.name}
            </StepButton>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
}
